import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import addTypesToVariables from '@/utils/conversions/addTypeToVariables';
import downloadTextFile from '@/utils/general/fileDownload/downloadTextFile';
import compareNetworks, { CompareOptionProps } from '@/utils/network/comparison/compareNetworks';
import { showError } from '@/utils/toast';
import { trpc } from '@/utils/trpc';
import getNetworkName from '../utils/getNetworkName';
function useNetworkComparison() {
  const $ = _c(2);
  const utils = trpc.useUtils();
  let t0;
  if ($[0] !== utils) {
    t0 = async (networkIds, options, networks) => {
      const {
        oldVariables,
        newVariables,
        oldDefaultTransitionHalfBandPercent,
        newDefaultTransitionHalfBandPercent
      } = await getNetworksVariablesForCompare(networkIds, utils);
      const {
        oldVariableStates,
        newVariableStates
      } = await getNetworksVariableStatesForCompare(networkIds, utils);
      const oldNetworkName = getNetworkName(networks, networkIds.oldNetworkId);
      const newNetworkName = getNetworkName(networks, networkIds.newNetworkId);
      const compareAssumptionSets = options.shownChanges?.assumptionSets;
      const {
        oldAssumptionSets,
        newAssumptionSets
      } = compareAssumptionSets ? await getAssumptionSetsForCompare(networkIds, utils) : {
        oldAssumptionSets: undefined,
        newAssumptionSets: undefined
      };
      const networkDiff = compareNetworks({
        oldNetwork: {
          variables: oldVariables,
          variableStates: oldVariableStates,
          assumptionSets: oldAssumptionSets,
          defaultTransitionHalfBandPercent: oldDefaultTransitionHalfBandPercent
        },
        newNetwork: {
          variables: newVariables,
          variableStates: newVariableStates,
          assumptionSets: newAssumptionSets,
          defaultTransitionHalfBandPercent: newDefaultTransitionHalfBandPercent
        }
      }, {
        oldNetworkName,
        newNetworkName
      }, options);
      downloadTextFile(networkDiff.getDiffText({
        displayPublicationStatus: options.displayPublicationStatus,
        displayAssumptionSets: compareAssumptionSets ?? false
      }), `${oldNetworkName}-vs-${newNetworkName}.txt`);
    };
    $[0] = utils;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
async function getNetworksVariableStatesForCompare({
  oldNetworkId,
  newNetworkId
}: {
  oldNetworkId: string;
  newNetworkId: string;
}, utils: ReturnType<typeof trpc.useUtils>) {
  try {
    const [oldVariableStates, newVariableStates] = await Promise.all([utils.network.getNetworkVariableStates.fetch({
      networkId: oldNetworkId
    }), utils.network.getNetworkVariableStates.fetch({
      networkId: newNetworkId
    })]);
    return {
      oldVariableStates,
      newVariableStates
    };
  } catch (error) {
    if (error instanceof Error) {
      showError('Failed to load variable states for comparison');
      throw new Error(`Failed to load variable states for comparison: ${error.message}`);
    }
    throw error;
  }
}
const getNetworksVariablesForCompare = async ({
  oldNetworkId,
  newNetworkId
}: {
  oldNetworkId: string;
  newNetworkId: string;
}, utils: ReturnType<typeof trpc.useUtils>) => {
  const [oldNetwork, newNetwork] = await Promise.all([utils.network.getNetwork.fetch({
    networkId: oldNetworkId
  }), utils.network.getNetwork.fetch({
    networkId: newNetworkId
  })]).catch(error => {
    if (error instanceof Error) {
      showError(`Failed to load networks for comparison`);
      throw new Error(`Failed to load networks for comparison: ${error.message}`);
    }
    throw error;
  });
  return {
    oldVariables: addTypesToVariables(oldNetwork.variables, oldNetwork.variableTypes),
    newVariables: addTypesToVariables(newNetwork.variables, newNetwork.variableTypes),
    oldDefaultTransitionHalfBandPercent: oldNetwork.defaultTransitionHalfBandPercent,
    newDefaultTransitionHalfBandPercent: newNetwork.defaultTransitionHalfBandPercent
  };
};
async function getAssumptionSetsForCompare({
  newNetworkId,
  oldNetworkId
}: {
  newNetworkId: string;
  oldNetworkId: string;
}, utils: ReturnType<typeof trpc.useUtils>) {
  const [oldAssumptionSets, newAssumptionSets] = await Promise.all([utils.assumptionSet.getAssumptionSetAggregates.fetch({
    networkId: oldNetworkId
  }), utils.assumptionSet.getAssumptionSetAggregates.fetch({
    networkId: newNetworkId
  })]).catch(error => {
    if (error instanceof Error) {
      showError(`Failed to load assumptionSets for comparison`);
      throw new Error(`Failed to load assumptionSets for comparison: ${error.message}`);
    }
    throw error;
  });
  return {
    oldAssumptionSets,
    newAssumptionSets
  };
}
export default useNetworkComparison;